'use client';

import React, { useState, useCallback, useRef } from 'react';
import moment from 'moment';
import { Popover, ArrowContainer } from 'react-tiny-popover';
import { PatternFormat } from 'react-number-format';

import RenderMaps from 'src/components/shared/map/map';

import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { updateVisibility } from 'src/redux/modals/slice';
import type { JobData } from 'src/types';
import type { PopoverPosition } from 'react-tiny-popover';

interface PopoverContentProps {
  position: PopoverPosition;
  childRect: DOMRect;
  popoverRect: DOMRect;
}

interface TenantInfoProps {
  job: JobData | Record<string, unknown>;
  isMakeReadyJob?: boolean;
}

const TenantInfo: React.FC<TenantInfoProps> = ({ job, isMakeReadyJob }) => {
  const dispatch = useAppDispatch();
  const type = useAppSelector((state) => state?.jobDetails?.slideOut?.type);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const location = (
    job as Record<string, unknown>
  )?.location as
    Record<string, unknown> | undefined;
  const tenantInfo = job?.tenant_info as Record<string, unknown> | undefined;

  const handleMouseEnter = useCallback(() => {
    openTimerRef.current = setTimeout(() => {
      setPopoverOpen(true);
    }, 200);
  }, []);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(openTimerRef?.current as ReturnType<typeof setTimeout>);
    setPopoverOpen(false);
  }, []);

  const popoverContent = useCallback((props: PopoverContentProps) => {
    if (location?.latitude && location?.longitude) {
      return (
        <ArrowContainer
          position={props.position}
          childRect={props.childRect}
          popoverRect={props.popoverRect}
          arrowColor="white"
          arrowSize={8}
          className="popover-arrow-container"
          arrowClassName="popover-arrow"
          style={{ zIndex: 99999 }}
        >
          <div className="maps">
            <RenderMaps
              center={{
                lat: Number(location?.latitude),
                lng: Number(location?.longitude),
              }}
              containerStyle={{
                width: '400px',
                height: '400px',
              }}
            />
          </div>
        </ArrowContainer>
      );
    }
    return <div />;
  }, [location?.latitude, location?.longitude]);

  return (
    <div className="job-content-data">
      {type === 'jobDetails' ? (
        <p className="mb-2">
          <span className="resident-address">
            {!isMakeReadyJob ? 'Address:' : ''}
            {' '}
            <span className="tenant-value">
              {(tenantInfo?.address as string) || ''}
            </span>
            {' '}

            <Popover
              isOpen={popoverOpen}
              positions={['left']}
              reposition={false}
              containerClassName="location-map-popover"
              boundaryElement={document.getElementsByClassName('lp-jd')[0] as HTMLElement}
              content={popoverContent}
            >
              <span
                className="btn-link view-map-btn"
                role="presentation"
                onClick={() => {
                  dispatch(
                    updateVisibility({
                      isVisible: true,
                      modalType: 'mapModal',
                      extraProps: {
                        modalData: {
                          address: tenantInfo?.address,
                          job_count: (job as Record<string, unknown>)?.job_history_count,
                          latitude: Number(location?.latitude),
                          longitude: Number(location?.longitude),
                        },
                      },
                    }),
                  );
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {' '}
                View Map
                {' '}
              </span>
            </Popover>
          </span>
        </p>
      ) : ''}
      {tenantInfo?.name? 
      <p className="mb-2">
        <span>
          Name:
          {' '}
          <span className="tenant-value">
            {(tenantInfo?.name as string) || ''}
          </span>
          {tenantInfo?.phone ? (
            <React.Fragment>
              {' '}
              {' | '}
              <span>
                Phone:
                {' '}
                <span className="tenant-value">
                  <a href={`tel:${tenantInfo?.phone}`}>
                    <PatternFormat
                      value={tenantInfo?.phone as string}
                      format="(###) ###-####"
                      displayType="text"
                    />
                  </a>
                </span>
              </span>
            </React.Fragment>
          ) : ''}
        </span>
      </p> :""
      }
      {tenantInfo?.move_in_date || tenantInfo?.move_out_date ? (
        <p className="mb-2">
          <span>
            {tenantInfo?.move_in_date ? (
              <React.Fragment>
                Move In:
                {' '}
                <span className="tenant-value">
                  {moment
                    ?.utc(tenantInfo?.move_in_date as string)
                    ?.format('MMM D, YYYY') || ''}
                </span>
              </React.Fragment>
            ) : ''}
            {tenantInfo?.move_in_date && tenantInfo?.move_out_date ? (
              <React.Fragment>
                {' '}
                {' | '}
              </React.Fragment>
            ) : ''}
            {tenantInfo?.move_out_date ? (
              <React.Fragment>
                Move Out:
                {' '}
                <span className="tenant-value">
                  {moment
                    ?.utc(tenantInfo?.move_out_date as string)
                    ?.format('MMM D, YYYY') || ''}
                </span>
              </React.Fragment>
            ) : ''}
          </span>
        </p>
      ) : ''}
    </div>
  );
};

export default TenantInfo;
